import { createTheme } from '@mui/material/styles';

// Light theme color palette
const lightPalette = {
  mode: 'light',
  themeMode: 'light',
  primary: {
    main: '#1E88E5', // Deep ocean blue for trust and exploration
    light: '#4FC3F7', // Sky blue for hover states
    dark: '#1565C0', // Darker blue for depth
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#FBC02D', // Warm gold for luxury and adventure
    light: '#FFCA28', // Lighter gold
    dark: '#F57F17', // Deeper amber
    contrastText: '#000000',
  },
  error: {
    main: '#E53935', // Professional red for errors
    light: '#EF5350', // Lighter red
    dark: '#B71C1C', // Darker red
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FF9800', // Orange for warnings, evoking sunsets
    light: '#FFB300', // Lighter orange
    dark: '#F57C00', // Deeper orange
    contrastText: '#000000',
  },
  info: {
    main: '#2E7D32', // Forest green for nature and calm
    light: '#4CAF50', // Lighter green
    dark: '#1B5E20', // Darker green
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#4CAF50', // Green for success, tied to nature
    light: '#81C784', // Lighter green
    dark: '#388E3C', // Darker green
    contrastText: '#FFFFFF',
  },
  text: {
    primary: '#0F172A', // Near-black for readability
    secondary: '#4B5563', // Cool gray for secondary text
    disabled: '#9CA3AF', // Light gray for disabled states
  },
  background: {
    default: '#F8FAFC', // Clean off-white for a professional look
    paper: '#FFFFFF', // Pure white for cards and surfaces
  },
  grey: {
    50: '#F9FAFB',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E2A44',
    900: '#0F172A',
  },
  common: {
    white: '#FFFFFF',
    black: '#000000',
  },
  divider: '#E2E8F0', // Light gray for dividers
  custom: {
    highlight: '#FFF3E0', // Warm beige for highlights (e.g., callouts)
    subtleBackground: '#F1F5F9', // Subtle gray for secondary backgrounds
    gradient: 'linear-gradient(135deg, #1E88E5 0%, #FBC02D 100%)', // Blue-to-gold gradient for travel vibe
  },
};

// Dark theme color palette
const darkPalette = {
  mode: 'dark',
  themeMode: 'dark',
  primary: {
    main: '#4FC3F7', // Sky blue for primary actions
    light: '#81D4FA', // Lighter blue
    dark: '#0288D1', // Deeper blue
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#FFCA28', // Gold for secondary actions
    light: '#FFD54F', // Lighter gold
    dark: '#FBC02D', // Warm amber
    contrastText: '#000000',
  },
  error: {
    main: '#EF5350', // Red for errors
    light: '#FF8A80', // Lighter red
    dark: '#D32F2F', // Darker red
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FFB300', // Orange for warnings
    light: '#FFCA28', // Lighter orange
    dark: '#EF6C00', // Deeper orange
    contrastText: '#000000',
  },
  info: {
    main: '#4CAF50', // Green for info, nature-inspired
    light: '#81C784', // Lighter green
    dark: '#2E7D32', // Darker green
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#66BB6A', // Green for success
    light: '#A5D6A7', // Lighter green
    dark: '#2E7D32', // Darker green
    contrastText: '#FFFFFF',
  },
  text: {
    primary: '#E2E8F0', // Off-white for readability
    secondary: '#94A3B8', // Light gray for secondary text
    disabled: '#6B7280', // Darker gray for disabled states
  },
  background: {
    default: '#0F172A', // Deep navy for a starry-night feel
    paper: '#1E2A44', // Slightly lighter navy for surfaces
  },
  grey: {
    50: '#1E2A44',
    100: '#334155',
    200: '#475569',
    300: '#64748B',
    400: '#94A3B8',
    500: '#CBD5E1',
    600: '#E2E8F0',
    700: '#F1F5F9',
    800: '#F9FAFB',
    900: '#FFFFFF',
  },
  common: {
    white: '#FFFFFF',
    black: '#000000',
  },
  divider: '#334155', // Dark gray for dividers
  custom: {
    highlight: '#374151', // Subtle gray for highlights
    subtleBackground: '#1E2A44', // Dark navy for secondary backgrounds
    gradient: 'linear-gradient(135deg, #4FC3F7 0%, #FFCA28 100%)', // Blue-to-gold gradient
  },
};

// Shared typography settings
const typography = {
  fontFamily: '"DM Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.015em',
    '@media (min-width:600px)': {
      fontSize: '3.5rem',
    },
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    '@media (min-width:600px)': {
      fontSize: '2.75rem',
    },
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '0em',
    '@media (min-width:600px)': {
      fontSize: '2.25rem',
    },
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.005em',
    '@media (min-width:600px)': {
      fontSize: '1.75rem',
    },
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
    '@media (min-width:600px)': {
      fontSize: '1.5rem',
    },
  },
  h6: {
    fontSize: '1.1rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.015em',
    '@media (min-width:600px)': {
      fontSize: '1.25rem',
    },
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.02em',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.6,
    letterSpacing: '0.02em',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.015em',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0.015em',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 700,
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'none',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase',
  },
};

// Shared component overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '10px 20px',
        textTransform: 'none',
        fontWeight: 600,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          background: (theme) => theme.palette.custom.gradient, // Gradient on hover
        },
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
      outlined: {
        borderWidth: 2,
        '&:hover': {
          borderWidth: 2,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}10, ${theme.palette.secondary.light}10)`,
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        backgroundColor: (theme) => theme.palette.background.paper,
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          backgroundColor: (theme) => theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
          '& fieldset': {
            borderColor: (theme) => theme.palette.grey[300],
          },
          '&:hover fieldset': {
            borderColor: (theme) => theme.palette.primary.light,
          },
          '&.Mui-focused fieldset': {
            borderColor: (theme) => theme.palette.primary.main,
            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}20`, // Subtle glow
          },
        },
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        backgroundColor: (theme) => theme.palette.background.paper,
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '12px 0',
          background: (theme) => theme.palette.custom.subtleBackground,
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        background: (theme) => theme.palette.custom.gradient, // Gradient for header
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        transition: 'box-shadow 0.3s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-2px)', // Lift effect for cards
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}20)`,
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      body: {
        transition: 'background-color 0.3s ease',
        '&::-webkit-scrollbar': {
          width: 8,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[600],
          borderRadius: 4,
        },
      },
    }),
  },
};

// Base theme options
const baseThemeOptions = {
  typography,
  components,
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
    duration: {
      standard: 300,
    },
  },
  zIndex: {
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
};

// Create themes
export const lightTheme = createTheme({
  palette: lightPalette,
  ...baseThemeOptions,
});

export const darkTheme = createTheme({
  palette: darkPalette,
  ...baseThemeOptions,
});